import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PhoneArrowUpRightIcon, TrashIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '../../services/api';

/**
 * Los números desde los que se llama a los deudores de esta company.
 *
 * Vive en el **perfil de la empresa**, dentro del bloque «Comunicación», y no en
 * Configuración: Configuración es solo de admin, y esto es auto-servicio — la
 * empresa conecta su número igual que conecta su WhatsApp o su dominio de correo.
 *
 * ⚠️ **La company destino es la del PERFIL que se está viendo** (`companyId`), no
 * la del usuario que mira. Así un admin puede darlo de alta por un cliente que
 * llama por teléfono, y la empresa puede hacerlo sola desde su propio perfil, con
 * el mismo componente. El backend además impone su propia company a quien no es
 * admin, así que el parámetro no es una vía para colar el número de otro.
 *
 * Con `own` es el bloque del GESTOR en su «Mi perfil»: en AR llama él con SU
 * número (docs/ar-numero-gestor-plan.md). El backend ya le devuelve solo los
 * suyos y le pone a él como dueño al darlo de alta.
 */

const STATUS = {
  pending_verification: { label: 'Pendiente de verificar', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  active: { label: 'Activo', cls: 'bg-green-50 text-green-800 border-green-200' },
  disconnected: { label: 'Desconectado', cls: 'bg-slate-50 text-slate-500 border-slate-200' },
};

/**
 * ¿Parece un móvil español? Desde la Orden TDF/149/2025 el operador del deudor
 * puede bloquear o marcar como spam una llamada que presenta un móvil (06-reparto
 * §4.1). Se acepta, pero se avisa.
 */
export const looksLikeMobile = (raw) => /^(?:\+?34)?[67]\d{8}$/.test(String(raw || '').replace(/[\s.\-()]/g, ''));

const CallPhonesSection = ({ companyId, own = false }) => {
  const [phones, setPhones] = useState([]);
  const [form, setForm] = useState({ phone_number: '', name: '', method: 'sms' });
  const [codes, setCodes] = useState({});
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!companyId && !own) return;
    try {
      const { data } = await api.get('/calls/phones');
      const all = Array.isArray(data?.data) ? data.data : [];
      // Un admin recibe todos; aquí solo interesan los de esta empresa. Al gestor
      // el backend ya le da solo los suyos.
      setPhones(own ? all : all.filter((p) => String(p.company_id) === String(companyId)));
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, [companyId, own]);

  useEffect(() => {
    load();
  }, [load]);

  const register = async (event) => {
    event.preventDefault();
    if (!form.phone_number.trim()) return;
    setBusy('new');
    try {
      await api.post('/calls/phones', own ? form : { ...form, company_id: companyId });
      toast.success('Hemos enviado un código a ese número');
      setForm({ phone_number: '', name: '', method: 'sms' });
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const verify = async (phone) => {
    const code = (codes[phone._id] || '').trim();
    if (!code) return;
    setBusy(phone._id);
    try {
      await api.post(`/calls/phones/${phone._id}/verify`, { code });
      toast.success('Número verificado');
      setCodes((c) => ({ ...c, [phone._id]: '' }));
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async (phone) => {
    setBusy(phone._id);
    try {
      await api.delete(`/calls/phones/${phone._id}`);
      toast.success('Número desconectado');
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className='flex items-center mb-2'>
        <PhoneArrowUpRightIcon className='h-4 w-4 text-slate-400 mr-1.5' />
        <span className='text-sm font-medium text-slate-600'>{own ? 'Tu número para llamar' : 'Llamadas'}</span>
      </div>

      {own && (
        <p className='mb-2 text-sm text-slate-500'>
          A los deudores de AR los llamas tú, presentando este número. Sin él no puedes abrir la sesión de
          llamadas.
        </p>
      )}

      {/*
        No es letra pequeña: quien espere recibir aquí las devoluciones de llamada
        se llevaría una sorpresa, y lo descubriría cuando un deudor le llamara de
        vuelta y no le entrase.
      */}
      <p className='mb-3 text-sm text-slate-500'>
        El número se queda en tu operador; solo se usa para{' '}
        <span className='font-medium text-slate-700'>llamar</span>. Las llamadas que te hagan a él seguirán
        entrando en tu centralita como hasta ahora.
      </p>

      <div className='space-y-2'>
        {phones.map((phone) => {
          const status = STATUS[phone.status] || STATUS.disconnected;
          return (
            <div
              key={phone._id}
              className='flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2'
            >
              <span className='font-mono text-sm text-slate-800'>{phone.phone_number}</span>
              {phone.nombre && <span className='text-sm text-slate-500'>{phone.nombre}</span>}
              {!own && phone.user_name && <span className='text-sm text-slate-500'>de {phone.user_name}</span>}
              <span className={`rounded-lg border px-2 py-0.5 text-xs font-medium ${status.cls}`}>
                {status.label}
              </span>

              {phone.status === 'pending_verification' && (
                <span className='ml-auto flex items-center gap-2'>
                  <input
                    type='text'
                    inputMode='numeric'
                    value={codes[phone._id] || ''}
                    onChange={(e) => setCodes({ ...codes, [phone._id]: e.target.value })}
                    placeholder='Código'
                    className='w-28 rounded-lg border border-slate-300 px-2 py-1 text-sm'
                  />
                  <button
                    type='button'
                    onClick={() => verify(phone)}
                    disabled={busy === phone._id}
                    className='rounded-lg bg-slate-900 px-3 py-1 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50'
                  >
                    Verificar
                  </button>
                </span>
              )}

              {phone.status === 'active' && (
                <button
                  type='button'
                  onClick={() => disconnect(phone)}
                  disabled={busy === phone._id}
                  className='ml-auto inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50'
                >
                  <TrashIcon className='h-4 w-4' aria-hidden='true' />
                  Desconectar
                </button>
              )}
            </div>
          );
        })}

        {phones.length === 0 && (
          <p className='text-sm text-slate-400'>
            {own
              ? 'Todavía no tienes número: no puedes llamar a expedientes AR hasta que des de alta el tuyo.'
              : 'Sin número propio: las llamadas de recobro salen desde los números de ClaimUp.'}
          </p>
        )}
      </div>

      <form onSubmit={register} className='mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto_auto]'>
        <input
          type='text'
          value={form.phone_number}
          onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
          placeholder='+34 930 00 00 00'
          className='rounded-lg border border-slate-300 px-3 py-2 text-sm'
        />
        <input
          type='text'
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder='Nombre (opcional)'
          className='rounded-lg border border-slate-300 px-3 py-2 text-sm'
        />
        <select
          value={form.method}
          onChange={(e) => setForm({ ...form, method: e.target.value })}
          className='rounded-lg border border-slate-300 px-3 py-2 text-sm'
        >
          <option value='sms'>Código por SMS</option>
          <option value='call'>Código por llamada</option>
        </select>
        <button
          type='submit'
          disabled={busy === 'new'}
          className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'
        >
          Añadir
        </button>
      </form>
      {looksLikeMobile(form.phone_number) && (
        <p className='mt-2 text-sm text-amber-700'>
          Es un móvil. Se puede usar, pero desde 2025 los operadores pueden bloquear o marcar como spam las
          llamadas que presentan un móvil. Un fijo de la empresa no tiene ese riesgo.
        </p>
      )}
    </div>
  );
};

export default CallPhonesSection;
